class AnnouncementBar extends HTMLElement {
  constructor() {
    super();
  }

  get els() {
    return this.querySelectorAll('[data-announcement]');
  }

  get countdownEls() {
    return this.querySelectorAll('[data-countdown]');
  }

  get linkEls() {
    return this.querySelectorAll('[data-announcement] a');
  }

  get navNext() {
    return this.querySelector('[data-nav="next"]');
  }

  get navPrev() {
    return this.querySelector('[data-nav="prev"]');
  }

  get navPause() {
    return this.querySelector('[data-nav="pause"]');
  }

  get freeShippingMessages() {
    return this.querySelectorAll('[data-free-shipping-message]');
  }

  get freeShippingThresholds() {
    return this.querySelectorAll('[data-announcement-threshold]');
  }

  connectedCallback() {
    // Liquid fallback for no active announcements
    if (!this.els.length) {
      this.parentElement.classList.add('hide');
      return;
    }

    // Primary settings
    this.hasFreeShippingBanner = this.dataset.hasFreeShippingBanner;
    this.indexActive = 0;
    this.indexLast = this.els.length - 1;

    // Secondary settings
    this.hasAutoplay = this.dataset.autoplay && this.dataset.autoplay > 0;
    this.hasCarousel = this.els.length > 1;

    // Setup and listeners
    if (this.els.length) this.setItemAttrs();
    if (this.countdownEls.length) this.handleCountdowns();
    if (this.hasCarousel && this.hasAutoplay) this.autoplayTimerStart();
    if (this.hasCarousel && this.hasAutoplay) this.handleAutoplayToggle();
    if (this.hasCarousel) this.handleNav();
    if (this.hasFreeShippingBanner) this.freeShippingBanner();
    this.handleAutoplayOnInteraction();
  }

  // Set index-based attributes of announcement items
  setItemAttrs() {
    this.els.forEach((el, i) => {
      const animationDuration = i === 0 ? 'var(--announcement-animation-duration, 300ms)' : '300ms';
      const opacity = i === 0 ? 'var(--announcement-opacity, 0)' : 0;
      const transformY = i === 0 ? 'var(--announcement-transform-y, 2px)' : '2px';

      el.dataset.index = i;
      if (i === 0) {
        el.classList.remove('hide');
        el.classList.add('active');
      }
      el.style.animation = `fadeIn ${animationDuration} forwards 1 ease-in-out`;
      el.style.opacity = opacity;
      el.style.transform = `var(--prefers-reduced-motion-none, translate3d(0, ${transformY}, 0))`;
    });
  }

  autoplayTimerStart() {
    this.timerIsPaused = false;
    this.timerIsStopped = false;

    this.timer = setInterval(() => {
      this.els[this.indexActive]?.classList.replace('active', 'hide');
      this.indexActive = this.indexActive + 1 > this.indexLast ? 0 : this.indexActive + 1;

      requestAnimationFrame(() => {
        this.els[this.indexActive]?.classList.replace('hide', 'active');
      });
    }, this.dataset.autoplay);
  }

  autoplayTimerPause() {
    if (!this.hasAutoplay || this.timerIsPaused) return;

    this.timerIsPaused = true;
    clearInterval(this.timer);
  }

  autoplayTimerStop() {
    if (!this.hasAutoplay || this.timerIsStopped) return;

    this.timerIsStopped = true;
    clearInterval(this.timer);
  }

  handleAutoplayToggle() {
    if (!this.navPause) return;

    this.navPause.classList.remove('hide');
    this.setAutoplayToggleState(true);

    this.navPause.addEventListener('click', () => {
      if (this.timerIsPaused || this.timerIsStopped) {
        this.autoplayTimerStart();
        this.setAutoplayToggleState(true);
      } else {
        this.autoplayTimerPause();
        this.setAutoplayToggleState(false);
      }
    });
  }

  setAutoplayToggleState(isPlaying) {
    if (!this.navPause) return;

    const label = isPlaying ? 'Pause announcements' : 'Play announcements';

    this.navPause.dataset.state = isPlaying ? 'playing' : 'paused';
    this.navPause.setAttribute('aria-pressed', String(!isPlaying));
    this.navPause.setAttribute('aria-label', label);
    this.navPause.setAttribute('title', label);
  }

  freeShippingBanner() {
    subscribe(PUB_SUB_EVENTS.cartUpdate, event => {
      if (event.source === 'product-form') return;
      this.updateFreeShippingBanner(event.cartData.total_price);
    });
  }

  handleAutoplayOnInteraction() {
    if (!this.hasAutoplay || !this.hasCarousel || this.timerIsStopped) return;

    this.addEventListener('click', () => {
      this.classList.add('interacted');
    });

    this.linkEls.forEach(el => {
      el.addEventListener('click', () => {
        this.autoplayTimerPause();
        this.setAutoplayToggleState(false);
      });
    });
  }

  handleNav() {
    [this.navNext, this.navPrev].forEach(el => {
      if (this.dataset.hideArrows === 'true') return;

      el.classList.remove('hide');

      el.addEventListener('click', () => {
        requestAnimationFrame(() => {
          this.autoplayTimerStop();
          this.setAutoplayToggleState(false);
          this.navUpdate(el.dataset.nav);
        });

        this.classList.add('interacted');
      });
    })
  }

  navUpdate(direction) {
    const nextIndex = this.indexActive + 1 > this.indexLast ? 0 : this.indexActive + 1;
    const prevIndex = this.indexActive - 1 < 0 ? this.indexLast : this.indexActive - 1;
    const newIndex = direction === 'prev' ? prevIndex : nextIndex;
    this.els[this.indexActive].classList.replace('active', 'hide');
    this.els[newIndex].classList.replace('hide', 'active');
    this.indexActive = newIndex;
  }

  now(timezone) {
    return Date.parse(typeof timezone !== 'undefined' ? new Date().toLocaleString('en-US', {timeZone: timezone}) : new Date())
  }

  updateFreeShippingBanner(total) {
    const away = Math.abs(total - window.freeShipping?.amount);
    const hasFreeShipping = total >= window.freeShipping?.amount;

    this.freeShippingMessages.forEach(el => {
      const type = el.dataset.freeShippingMessage;

      if (hasFreeShipping) {
        el.classList[type === 'met' ? 'remove' : 'add']('hide');
      } else {
        el.classList[type === 'met' ? 'add' : 'remove']('hide');
        this.freeShippingThresholds.forEach(el => el.innerHTML = window.formatMoney(away).replace('.00', ''));
      }
    });
  }

  /***
  * Countdown Timers
  */
  handleCountdowns() {
    this.countdownTimers = [];
    this.countdownEls.forEach((el, index) => {
      let data = null;

      try {
        // Run times are set in the Announcement Item block
        data = JSON.parse(el.parentElement.dataset.runTimes);
      } catch (error) {
        console.error('Unable to parse countdown settings:', error);
      }

      if (data) {
        const ends = new Date(data.end).toLocaleString('en-US', { timeZone: 'America/Los_Angeles' });
        const starts = new Date(data.start).toLocaleString('en-US', { timeZone: 'America/Los_Angeles' });

        this.countdownCreate({
          el,
          ends,
          endDate: data.end ? new Date(ends).getTime() : null,
          startDate: data.start ? new Date(starts).getTime() : null,
          timezone: 'America/Los_Angeles',
        }, index);
      }
    });
  }

  /**
  * Create countdown
  */
  countdownCreate(settings, index) {
    const endDate = settings.endDate + this.timezoneOffset(settings);
    const today = this.now(settings.timezone);
    const dateHasPassed = Math.sign(endDate - today) >= 0;

    settings.el.title = `Ends ${settings.ends}`;

    if (settings.endDate && dateHasPassed) {
      this.countdownUpdate(settings, settings.el);
      this.countdownTimers[index] = setInterval(() => this.countdownUpdate(settings, settings.el, index), 1000);
    } else {
      settings.el.innerHTML = `
        <span class="countdown__hours">00h:</span>
        <span class="countdown__minutes">00m:</span>
        <span class="countdown__seconds">00s</span>
      `;
    }
  }

  /**
  * Update Timer
  */
  countdownUpdate(settings, $timer, index) {
    const now = this.now(settings.timezone);
    const endDate = settings.endDate + this.timezoneOffset(settings);
    const distance = endDate - now;

    const days = Math.floor(distance / (1000 * 60 * 60 * 24))
      .toLocaleString(undefined, { minimumIntegerDigits: 2 });
    const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
      .toLocaleString(undefined, { minimumIntegerDigits: 2 });
    const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60))
      .toLocaleString(undefined, { minimumIntegerDigits: 2 });
    const seconds = Math.floor((distance % (1000 * 60)) / 1000)
      .toLocaleString(undefined, { minimumIntegerDigits: 2 });

    if (days <= 0 && hours <= 0 && minutes <= 0 && seconds <= 0) {
      $timer.innerHTML = `
        <span class="countdown__hours">00h:</span>
        <span class="countdown__minutes">00m:</span>
        <span class="countdown__seconds">00s</span>
      `;
      clearInterval(this.timers[index]);
    } else {
      $timer.innerHTML = `
        ${days < 1 ? '' : `<span class="countdown__days">${days}d:</span>`}
        <span class="countdown__hours">${hours}h:</span>
        <span class="countdown__minutes">${minutes}m</span>
        ${days > 0 ? '' : `:<span class="countdown__seconds">${seconds}s</span>`}
      `;
    }
  }

  timezoneOffset(settings) {
    return Math.abs(this.now(settings.timezone) - this.now()) // difference in milliseconds
  }
}

if (!customElements.get('announcement-bar')) {
  customElements.define('announcement-bar', AnnouncementBar);
}
